import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { NotificationCenter } from "./notification-center";
import { InboxBellBadge } from "../../../modules/inbox/presentation/components/inbox-bell-badge";
import { NavLink, useNavigate } from "react-router-dom";
import {
  IconBell,
  IconHeart,
  IconMenu2,
  IconMessageCircle,
  IconSettings,
  IconShieldCheck,
  IconShoppingCart,
  IconSwitchHorizontal,
  IconX,
} from "@tabler/icons-react";
import { clsx } from "clsx";
import logo from "../../../assets/images/logo.svg";
import { AppRoutes } from "../../../common/routes/app_routes";
import { useCartStore } from "../../../hooks/stores/useCartStore";
import { useCartUiStore } from "../../../hooks/stores/useCartUiStore";
import { useWishlistUiStore } from "../../../hooks/stores/useWishlistUiStore";
import { useComparisonSaveUiStore } from "../../../hooks/stores/useComparisonSaveUiStore";
import { CartPopover } from "../../../modules/cart/presentation/components/cart-popover";
import { CartFlyLayer } from "../../../modules/cart/presentation/components/cart-fly-layer";
import { WishlistFlyLayer } from "../../../modules/products/presentation/components/wishlist-fly-layer";
import { ComparisonSaveFlyLayer } from "../../../modules/comparisons/presentation/components/comparison-save-fly-layer";
import { NEW_AI_SEARCH_SESSION_STATE } from "../../../modules/ai-search/hooks/useAiSearchSessionKey";
import type { UserDto } from "../../../api";
import { SearchBar } from "../input";
import { Text } from "../typography";
import { getUserInitials, UserMenu, type UserMenuEntry } from "./user-menu";
import { UserAvatar } from "./user-avatar";

type NavItem = { label: string; to: string; matchExact?: boolean; state?: unknown };

const BUYER_NAV: NavItem[] = [
  // Always lands on a fresh assistant, even from an open conversation.
  { label: "Home", to: AppRoutes.AI_SEARCH, state: NEW_AI_SEARCH_SESSION_STATE },
  { label: "Dashboard", to: AppRoutes.DASHBOARD },
  { label: "Catalog", to: AppRoutes.PRODUCTS, matchExact: true },
  { label: "Quotations", to: "/quotations" },
  { label: "Orders", to: AppRoutes.ORDERS },
  { label: "Loyalty Points", to: AppRoutes.LOYALTY_POINTS },
];

const BUYER_MENU_ENTRIES: UserMenuEntry[] = [
  {
    label: "Wishlist",
    icon: <IconHeart size={16} />,
    to: AppRoutes.WISHLIST,
  },
  {
    label: "Warranty Claims",
    icon: <IconShieldCheck size={16} />,
    to: AppRoutes.MY_WARRANTY_CLAIMS,
  },
  {
    label: "My Conversations",
    icon: <IconMessageCircle size={16} />,
    to: AppRoutes.MY_CONVERSATIONS,
  },
  {
    label: "My Comparisons",
    icon: <IconSwitchHorizontal size={16} />,
    to: AppRoutes.MY_COMPARISONS,
  },
  {
    label: "Account",
    icon: <IconSettings size={16} />,
    to: AppRoutes.ACCOUNT,
  },
];

interface BuyerTopNavProps {
  user: UserDto;
  onLogout: () => void;
}

export function BuyerTopNav({ user, onLogout }: BuyerTopNavProps) {
  const navigate = useNavigate();
  const itemCount = useCartStore((s) => s.getTotalItems());
  const toggleCart = useCartUiStore((s) => s.toggle);
  const setCartAnchor = useCartUiStore((s) => s.setCartAnchor);
  const cartButtonRef = useRef<HTMLButtonElement>(null);
  const bellButtonRef = useRef<HTMLButtonElement>(null);
  const wishlistButtonRef = useRef<HTMLButtonElement>(null);
  const setWishlistAnchor = useWishlistUiStore((s) => s.setWishlistAnchor);
  const setComparisonSaveAnchor = useComparisonSaveUiStore((s) => s.setAnchor);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const [searchValue, setSearchValue] = useState("");
  const [notifOpen, setNotifOpen] = useState(false);
  // Below lg the search and the page links move into a hamburger panel.
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  const handleCartClick = useCallback(() => toggleCart(), [toggleCart]);

  const handleSearch = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      if (!trimmed) return;
      setSearchValue("");
      setMenuOpen(false);
      // A header search starts a new conversation — it isn't a follow-up to
      // whatever chat happens to be open on /ai-search.
      navigate(`${AppRoutes.AI_SEARCH}?q=${encodeURIComponent(trimmed)}`, {
        state: NEW_AI_SEARCH_SESSION_STATE,
      });
    },
    [navigate],
  );

  useEffect(() => {
    setCartAnchor(cartButtonRef.current);
    return () => setCartAnchor(null);
  }, [setCartAnchor]);

  useEffect(() => {
    setWishlistAnchor(wishlistButtonRef.current);
    return () => setWishlistAnchor(null);
  }, [setWishlistAnchor]);

  useEffect(() => {
    setComparisonSaveAnchor(userMenuRef.current);
    return () => setComparisonSaveAnchor(null);
  }, [setComparisonSaveAnchor]);

  const initials = getUserInitials(user.name);
  const iconButtonClassName =
    "relative inline-flex size-10 items-center justify-center text-textBody transition-colors hover:text-textDark";

  return (
    <>
      <header className="sticky top-0 z-[70] w-full border-b border-borderDefault bg-bgWhite print:hidden">
        <div className="flex w-full items-center justify-between px-4 py-3 lg:px-8">
          <div className="flex items-center gap-8">
            <button
              type="button"
              onClick={() => navigate(AppRoutes.INDEX)}
              aria-label="Procurtal home"
              className="flex items-center"
            >
              <img src={logo} alt="Procurtal" className="h-9" />
            </button>

            <SearchBar
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onSearch={handleSearch}
              showSearchButton={false}
              placeholder="Ask Procurtal"
              aria-label="Ask Procurtal"
              wrapperClassName="hidden lg:flex w-[300px] rounded-full border-borderDark bg-bgLight px-3 py-1"
            />

            <nav className="hidden items-center lg:flex">
              {BUYER_NAV.map((item) => (
                <NavLink
                  key={item.label}
                  to={item.to}
                  state={item.state}
                  end={item.matchExact}
                  className={({ isActive }) =>
                    clsx(
                      "rounded-xl px-2.5 py-2.5 transition-colors",
                      isActive
                        ? "text-accentCyan700 underline underline-offset-4"
                        : "text-textBody hover:text-textDark",
                    )
                  }
                >
                  <Text variant="button" inline tone="inherit">
                    {item.label}
                  </Text>
                </NavLink>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-1 lg:gap-3">
            <div className="relative">
              <button
                ref={bellButtonRef}
                type="button"
                onClick={() => setNotifOpen((v) => !v)}
                aria-label="Notifications"
                aria-expanded={notifOpen}
                className={iconButtonClassName}
              >
                <IconBell size={24} />
                <InboxBellBadge />
              </button>
              <NotificationCenter
                isOpen={notifOpen}
                onClose={() => setNotifOpen(false)}
                anchorRef={bellButtonRef}
              />
            </div>

            <button
              ref={wishlistButtonRef}
              type="button"
              onClick={() => navigate(AppRoutes.WISHLIST)}
              aria-label="Wishlist"
              className={clsx(iconButtonClassName, "max-lg:hidden")}
            >
              <IconHeart size={24} />
            </button>

            <div className="relative">
              <button
                ref={cartButtonRef}
                type="button"
                onClick={handleCartClick}
                aria-label={`Shopping cart with ${itemCount} items`}
                className="relative inline-flex size-10 items-center justify-center text-textDark transition-colors hover:text-textBody"
              >
                <IconShoppingCart size={24} />
                {itemCount > 0 && (
                  <span className="absolute -right-1 -top-1 inline-flex min-w-[18px] items-center justify-center rounded-full bg-accentError px-1 text-[11px] font-bold text-white">
                    {itemCount > 99 ? "99+" : itemCount}
                  </span>
                )}
              </button>
              <CartPopover />
            </div>

            <div ref={userMenuRef}>
              <UserMenu
                name={user.name ?? ""}
                email={user.email ?? ""}
                initials={initials}
                entries={BUYER_MENU_ENTRIES}
                onLogout={onLogout}
                trigger={<UserAvatar initials={initials} />}
              />
            </div>

            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              className={clsx(iconButtonClassName, "lg:hidden")}
            >
              {menuOpen ? <IconX size={24} /> : <IconMenu2 size={24} />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="flex flex-col gap-2 border-t border-borderDefault px-4 pb-4 pt-3 lg:hidden">
            <SearchBar
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onSearch={handleSearch}
              showSearchButton={false}
              placeholder="Ask Procurtal"
              aria-label="Ask Procurtal"
              wrapperClassName="w-full rounded-full border-borderDark bg-bgLight px-3 py-1"
            />
            <nav className="flex flex-col">
              {BUYER_NAV.map((item) => (
                <NavLink
                  key={item.label}
                  to={item.to}
                  state={item.state}
                  end={item.matchExact}
                  className={({ isActive }) =>
                    clsx(
                      "rounded-xl px-2.5 py-2.5 transition-colors",
                      isActive ? "bg-bgLight text-accentCyan700" : "text-textBody hover:text-textDark",
                    )
                  }
                >
                  <Text variant="button" inline tone="inherit">
                    {item.label}
                  </Text>
                </NavLink>
              ))}
            </nav>
          </div>
        )}
      </header>
      <CartFlyLayer />
      <WishlistFlyLayer />
      <ComparisonSaveFlyLayer />
    </>
  );
}

export default BuyerTopNav;
